import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUndo,
    faEye,
    faSearch,
    faRefresh,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faBox,
    faTag,
    faUser,
    faPhone,
    faDollarSign,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faShoppingCart,
    faStore,
    faReceipt,
    faHistory,
    faMoneyBillWave,
    faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>Switch Shop</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>Loading shops...</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>No shops available</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>Current</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ReturnList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [returns, setReturns] = useState([]);
    const [filteredReturns, setFilteredReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    
    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr'
    });

    const toastTimerRef = useRef(null);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [filterType, setFilterType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const totalPages = Math.ceil(filteredReturns.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentReturns = filteredReturns.slice(startIndex, endIndex);

    // Calculate totals
    const totalReturns = filteredReturns.length;
    const totalAmount = filteredReturns.reduce((sum, ret) => sum + (parseFloat(ret.grand_total) || 0), 0);
    const totalRefund = filteredReturns.reduce((sum, ret) => sum + (parseFloat(ret.refund_amount) || 0), 0);

    // Function to show toast
    const showToast = (type, message) => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction
        });

        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            setCurrentTheme(theme);
            applyTheme(theme);
        };

        window.addEventListener('storage', handleThemeChange);
        return () => window.removeEventListener('storage', handleThemeChange);
    }, [currentTheme]);

    // Apply RTL direction when language changes
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    // Fetch returns when shop changes
    useEffect(() => {
        if (user && user.username) {
            if (currentShop) {
                fetchReturns();
                fetchBusinessInfo();
                fetchAvailableShops();
            } else {
                setLoading(false);
            }
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters when returns, search term, or filter type changes
    useEffect(() => {
        applyFilters();
    }, [returns, searchTerm, filterType, startDate, endDate]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', 'Shop switched successfully');
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            No shop selected
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>Switch Shop</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="shop-indicator-modern active">
                <div className="shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="shop-details">
                        <span className="shop-name">{currentShop.shop_name}</span>
                        <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="shop-info-note">
                    Showing returns from this shop
                </div>
                <button
                    className="btn-modern btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>Switch Shop</span>
                </button>
            </div>
        );
    };

    // Fetch business info
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.data?.businessname || '',
                    bussinesslogo: data.data?.bussinesslogo || '',
                    businessaddress: data.data?.businessaddress || '',
                    fullname: data.data?.fullname || '',
                    email: data.data?.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
        }
    };

    const fetchReturns = async () => {
        if (!currentShop) {
            showToast('error', 'No shop selected. Please select a shop first.');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            // Get returns based ONLY on shop_id - username is optional
            const response = await fetch('https://ourzestapp.online/oz_backend/return.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    shop_id: currentShop.shop_id  // Only shop_id is required
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setReturns(data.data || []);
                if (data.data.length === 0) {
                    showToast('info', 'No returns found in this shop');
                }
            } else {
                showToast('error', data.message || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching returns:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...returns];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(ret =>
                ret.return_number?.toLowerCase().includes(term) ||
                ret.customer_name?.toLowerCase().includes(term) ||
                ret.customer_phone?.toLowerCase().includes(term)
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(ret => {
                if (!ret.date) return false;
                const retDate = new Date(ret.date);
                retDate.setHours(0, 0, 0, 0);
                return retDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(ret => {
                if (!ret.date) return false;
                const retDate = new Date(ret.date);
                return retDate <= end;
            });
        }

        setFilteredReturns(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterType('all');
        setStartDate('');
        setEndDate('');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleView = async (returnRecord) => {
        setSelectedReturn(returnRecord);
        
        if ((!returnRecord.items || returnRecord.items.length === 0) && returnRecord.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/return.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: returnRecord.id,
                        shop_id: currentShop.shop_id  // Only shop_id is required
                    })
                });

                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedReturn(data.data);
                }
            } catch (error) {
                console.error('Error fetching return details:', error);
            }
        }
        
        setShowViewModal(true);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleExportPDF = async () => {
        if (filteredReturns.length === 0) {
            showToast('error', 'No data to export!');
            return;
        }

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#10b981';

            function hexToRgba(hex, alpha = 1) {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }

            const formatDateGlobal = (dateString) => {
                if (!dateString) return '-';
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const currentDate = () => {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const currentTime = () => {
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                return `${hours}:${minutes}`;
            };

            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();

            const rowsPerPage = 20;
            const totalRows = filteredReturns.length;
            const totalPages = Math.ceil(totalRows / rowsPerPage);

            const headerHTML = `
                <div style="margin-bottom: 10px; text-align: center;">
                    <h1 style="color: ${primaryColor}; font-size: 20px; margin: 0 0 5px 0;">
                        ${businessInfo.businessname || 'Returns List'}
                    </h1>
                    <div style="color: #666; font-size: 12px;">
                        Returns Report - ${currentDate()} ${currentTime()}
                        ${currentShop ? ` | Shop: ${currentShop.shop_name}` : ''}
                    </div>
                    <div style="display: flex; justify-content: center; gap: 20px; margin-top: 10px;">
                        <div style="background: ${hexToRgba(primaryColor, 0.1)}; padding: 5px 15px; border-radius: 5px;">
                            <div style="font-size: 11px; color: #666;">Total Returns</div>
                            <div style="font-size: 16px; font-weight: 700; color: ${primaryColor};">${totalReturns}</div>
                        </div>
                        <div style="background: ${hexToRgba(primaryColor, 0.1)}; padding: 5px 15px; border-radius: 5px;">
                            <div style="font-size: 11px; color: #666;">Total Amount</div>
                            <div style="font-size: 16px; font-weight: 700; color: #059669;">Rs. ${totalAmount.toFixed(2)}</div>
                        </div>
                        <div style="background: #fee2e2; padding: 5px 15px; border-radius: 5px;">
                            <div style="font-size: 11px; color: #991b1b;">Total Refund</div>
                            <div style="font-size: 16px; font-weight: 700; color: #dc2626;">Rs. ${totalRefund.toFixed(2)}</div>
                        </div>
                    </div>
                </div>
            `;

            const headerCellStyle = `
                padding: 8px 4px;
                border: none;
                text-align: center;
                font-weight: 600;
                font-size: 9px;
                background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.8)});
                color: white;
            `;

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 8px;">
                    <thead>
                        <tr>
                            <th style="${headerCellStyle}">S.No</th>
                            <th style="${headerCellStyle}">Return #</th>
                            <th style="${headerCellStyle}">Date</th>
                            <th style="${headerCellStyle}">Customer</th>
                            <th style="${headerCellStyle}">Items</th>
                            <th style="${headerCellStyle}">Total</th>
                            <th style="${headerCellStyle}">Refund</th>
                            <th style="${headerCellStyle}">Reason</th>
                            <th style="${headerCellStyle}">Added By</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, totalRows);
                const pageReturns = filteredReturns.slice(startRow, endRow);

                const tableRowsHTML = pageReturns.map((ret, index) => {
                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #f0f0f0;">${startRow + index + 1}</td>
                            <td style="padding: 5px 3px; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">${ret.return_number || '-'}</td>
                            <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #f0f0f0;">${formatDateGlobal(ret.date)}</td>
                            <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #f0f0f0;">
                                <div>${ret.customer_name || 'Walk-in'}</div>
                                ${ret.customer_phone ? `<div style="font-size: 7px;">${ret.customer_phone}</div>` : ''}
                            </td>
                            <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-block; padding: 2px 6px; background: #f3e8ff; border-radius: 12px;">
                                    ${ret.item_count || 0}
                                </div>
                            </td>
                            <td style="padding: 5px 3px; text-align: center; font-weight: 600; border-bottom: 1px solid #f0f0f0;">Rs. ${parseFloat(ret.grand_total || 0).toFixed(2)}</td>
                            <td style="padding: 5px 3px; text-align: center; font-weight: 600; color: #dc2626; border-bottom: 1px solid #f0f0f0;">Rs. ${parseFloat(ret.refund_amount || 0).toFixed(2)}</td>
                            <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #f0f0f0;">${ret.return_reason || '-'}</td>
                            <td style="padding: 5px 3px; text-align: center; border-bottom: 1px solid #f0f0f0;">${ret.username || '-'}</td>
                        </tr>
                    `;
                }).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';

                const pageHTML = `
                    <div style="width: 1200px; padding: 15px; font-family: Arial, sans-serif; background: white;">
                        ${headerHTML}
                        ${tableHTML}
                        <div style="margin-top: 10px; text-align: center; font-size: 8px; color: #6b7280;">
                            Generated by: ${businessInfo.fullname || user?.username || ''} | Returns Report | Page ${pageNum} of ${totalPages}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '1200px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff'
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, 190));
            }

            const fileName = `Returns_List_${currentDate()}.pdf`;
            pdf.save(fileName);

            showToast('success', `PDF exported successfully with ${filteredReturns.length} return records!`);

        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '-';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faUndo} />
                    </div>
                    <div className="header-text">
                        <h1>Returns List</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `Shop: ${currentShop.shop_name}`
                                    : 'My Returns'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Summary Stats */}
            <div className="summary-stats-modern">
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>
                        <FontAwesomeIcon icon={faUndo} style={{ color: '#dc2626' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totalReturns}</div>
                        <div className="stat-label">Total Returns</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                        <FontAwesomeIcon icon={faDollarSign} style={{ color: '#10b981' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalAmount)}</div>
                        <div className="stat-label">Total Amount</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#ef4444' }} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalRefund)}</div>
                        <div className="stat-label">Total Refund</div>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={getTodayDate()}
                            className="filter-input compact"
                            placeholder="Start Date"
                        />
                    </div>
                    <div className="filter-group compact">
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={getTodayDate()}
                            className="filter-input compact"
                            placeholder="End Date"
                        />
                    </div>
                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            {/* <FontAwesomeIcon icon={faSearch} className="search-icon" /> */}
                            <input
                                type="text"
                                placeholder="Search by return #, customer..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>
                    <div className="filter-group compact">
                        <button
                            onClick={fetchReturns}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title="Refresh"
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">Refresh</span>
                        </button>
                    </div>
                    <div className="filter-group compact">
                        <button
                            onClick={handleExportPDF}
                            className="export-btn compact"
                            disabled={loading || filteredReturns.length === 0}
                            title="Export PDF"
                        >
                            <FontAwesomeIcon icon={faFilePdf} />
                            <span className="btn-text">PDF</span>
                        </button>
                    </div>
                    {(searchTerm || startDate || endDate) && (
                        <div className="filter-group compact">
                            <button
                                onClick={resetFilters}
                                className="clear-filters-btn"
                                title="Clear Filters"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>Clear</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>Loading returns data...</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>No Shop Selected</h3>
                        <p>Please select a shop to view returns</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>Switch Shop</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">S.No</th>
                                            <th className="centered-cell">Return #</th>
                                            <th className="centered-cell">Date</th>
                                            <th className="centered-cell">Customer</th>
                                            <th className="centered-cell">Items</th>
                                            <th className="centered-cell">Total</th>
                                            <th className="centered-cell">Refund</th>
                                            <th className="centered-cell">Reason</th>
                                            <th className="centered-cell">Added By</th>
                                            <th className="actions-column centered-cell">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentReturns.map((ret, index) => (
                                            <tr key={ret.id}>
                                                <td className="id-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                        <span className="cell-text">{startIndex + index + 1}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faReceipt} className="cell-icon" />
                                                        <span className="cell-text" style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#dc2626' }}>
                                                            {ret.return_number}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                        <span className="cell-text">{ret.date}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                        <span className="cell-text">{ret.customer_name || 'Walk-in'}</span>
                                                        {ret.customer_phone && (
                                                            <div className="cell-subtext">
                                                                <FontAwesomeIcon icon={faPhone} size="xs" />
                                                                <span>{ret.customer_phone}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="items-badge">
                                                            <FontAwesomeIcon icon={faBox} />
                                                            <span>{ret.item_count || 0}</span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="grand-total" style={{ color: '#059669', fontWeight: 'bold' }}>
                                                            {formatCurrency(ret.grand_total)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="refund-amount" style={{ color: '#dc2626', fontWeight: 'bold' }}>
                                                            {formatCurrency(ret.refund_amount)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="reason-text">{ret.return_reason || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="added-by-badge">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                            <span className="cell-text">{ret.username || '-'}</span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="actions-cell-modern centered-cell">
                                                    <div className="cell-content centered-content actions-content">
                                                        <button
                                                            onClick={() => handleView(ret)}
                                                            className="action-btn-modern view-btn-modern"
                                                            title="View Details"
                                                        >
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentReturns.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faUndo} />
                                        <p>No returns found in this shop</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredReturns.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>Showing {startIndex + 1}-{Math.min(endIndex, filteredReturns.length)} of {filteredReturns.length} returns</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | Shop: {currentShop.shop_name}
                                    </span>
                                )}
                                <span style={{ marginLeft: '20px', color: '#dc2626', fontWeight: '600' }}>
                                    Total Refund: {formatCurrency(totalRefund)}
                                </span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>Previous</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>Next</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedReturn && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon" style={{ background: '#dc2626' }}>
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="modal-title">
                                <h3>Return Details</h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="detail-section">
                                <h4 className="section-title">Return Information</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">Return #:</span>
                                        <span className="detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#dc2626' }}>
                                            {selectedReturn.return_number}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">Date:</span>
                                        <span className="detail-value">{selectedReturn.date}</span>
                                    </div>
                                    {selectedReturn.original_bill_number && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">Original Invoice:</span>
                                            <span className="detail-value">{selectedReturn.original_bill_number}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">Customer:</span>
                                        <span className="detail-value">{selectedReturn.customer_name || 'Walk-in Customer'}</span>
                                    </div>
                                    {selectedReturn.customer_phone && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">Phone:</span>
                                            <span className="detail-value">{selectedReturn.customer_phone}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">Refund Method:</span>
                                        <span className="detail-value">{selectedReturn.payment_method?.toUpperCase() || 'CASH'}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">Added By:</span>
                                        <span className="detail-value">{selectedReturn.username || '-'}</span>
                                    </div>
                                    {selectedReturn.return_reason && (
                                        <div className="detail-row-modern full-width">
                                            <span className="detail-label">Reason:</span>
                                            <span className="detail-value notes-text">{selectedReturn.return_reason}</span>
                                        </div>
                                    )}
                                    {selectedReturn.notes && (
                                        <div className="detail-row-modern full-width">
                                            <span className="detail-label">Notes:</span>
                                            <span className="detail-value notes-text">{selectedReturn.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Items Section */}
                            {selectedReturn.items && selectedReturn.items.length > 0 && (
                                <div className="detail-section">
                                    <h4 className="section-title">Returned Items</h4>
                                    <div className="items-table-container">
                                        <table className="items-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Product</th>
                                                    <th>Batch</th>
                                                    <th>Unit</th>
                                                    <th>Price</th>
                                                    <th>Qty</th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedReturn.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{item.product_name}</td>
                                                        <td className="batch-cell">{item.batch_no}</td>
                                                        <td>{item.unit}</td>
                                                        <td className="amount-cell">{formatCurrency(item.price)}</td>
                                                        <td className="amount-cell">{parseFloat(item.quantity).toFixed(2)}</td>
                                                        <td className="amount-cell total">{formatCurrency(item.total)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="6" className="text-right">Subtotal:</td>
                                                    <td className="amount-cell">{formatCurrency(selectedReturn.subtotal)}</td>
                                                </tr>
                                                {parseFloat(selectedReturn.tax) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">Tax:</td>
                                                        <td className="amount-cell">{formatCurrency(selectedReturn.tax)}</td>
                                                    </tr>
                                                )}
                                                {parseFloat(selectedReturn.discount) > 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-right">Discount:</td>
                                                        <td className="amount-cell">-{formatCurrency(selectedReturn.discount)}</td>
                                                    </tr>
                                                )}
                                                <tr className="grand-total-row">
                                                    <td colSpan="6" className="text-right">Grand Total:</td>
                                                    <td className="amount-cell grand-total">{formatCurrency(selectedReturn.grand_total)}</td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="6" className="text-right">Refund Amount:</td>
                                                    <td className="amount-cell refund" style={{ color: '#dc2626', fontWeight: 'bold' }}>
                                                        {formatCurrency(selectedReturn.refund_amount)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReturnList;